import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { SystemDescriptions } from "src/common/descriptions"
import { Customer } from "src/common/models/customer"
import { PageCard } from "src/common/styles/styles"
import { Urls } from "src/common/urls"
import { SearchableTable } from "src/components/SearchableTable"
import useUserPermission from "src/hooks/useUserPermissions"
import { RootState } from "src/state/reducer"
import { GET_CUSTOMERS_API } from "../state/actionTypes"
import { ColumnsType } from "antd/es/table"
import { ColumnsKeys, TABLE_COLUMNS } from "./config"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Space, Tooltip } from "antd"
import { EditOutlined, ProfileOutlined } from '@ant-design/icons'
import { useEffect, useState } from "react"
import { getCustomersApi } from "../state/action"
import { USER_SCOPES } from "src/hooks/UserPermission"

export type ReduxProps = {
    customers?: Customer[],
    isLoadingCustomers: boolean,
}

export enum PageView {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export const Customers = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { validateUserScopeForPage } = useUserPermission()

    const [pageView, setPageView] = useState(PageView.ACTIVE)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        customers: state.customer.customers,
        isLoadingCustomers: serviceLoading(state, [GET_CUSTOMERS_API]),
    }))

    const descriptions = SystemDescriptions.PAGES.CUSTOMER.CUSTOMERS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const { isUserAllowedToPerformAdminActions: userAdminActions } = useUserPermission()

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.CUSTOMER_READ)
        dispatch(getCustomersApi({ isActive: true }))
    }, [])

    const handleActionClick = () => {
        navigate(Urls.FRONTEND.CUSTOMER.CREATE)
    }

    const tblColumns: ColumnsType<Customer> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: Customer) => {
                const handleEditClick = (customerId: number) => {
                    navigate(Urls.FRONTEND.CUSTOMER.EDIT_PAGE(customerId))
                }

                const handleViewClick = (customerId: number) => {
                    navigate(Urls.FRONTEND.CUSTOMER.PROFILE_PAGE(customerId))
                }

                return <Space size="middle">
                    <Tooltip title={commonDescriptions.ACTIONS.VIEW} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.id!)}
                        />
                    </Tooltip>
                    {
                        userAdminActions
                            ?
                            <Tooltip title={commonDescriptions.ACTIONS.EDIT} >
                                <Button type="primary" ghost shape="circle" size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditClick(record.id!)}
                                />
                            </Tooltip>
                            : null
                    }
                </Space>
            },
        },
    ]

    const renderExtra = () => {
        return <Button
            color={pageView !== PageView.ACTIVE ? "default" : "danger"}
            variant="filled"
            size="small"
            onClick={() => {
                dispatch(getCustomersApi({
                    isActive: pageView !== PageView.ACTIVE ? true : false,
                }))

                setPageView(
                    pageView === PageView.ACTIVE
                        ? PageView.INACTIVE
                        : PageView.ACTIVE
                )
            }}
        >
            {
                pageView === PageView.ACTIVE
                    ? descriptions.VIEW_INACTIVES
                    : descriptions.VIEW_ACTIVES
            }
        </Button>
    }

    return (
        < PageCard
            size="small"
            title={descriptions.TITLE}
            extra={renderExtra()}
        >
            <SearchableTable
                showSearch
                showActionButton={userAdminActions}
                actionButtonLabel={descriptions.TABLE.ACTION_BUTTON}
                actionButtonOnClick={handleActionClick}
                showLoader={reduxProps.isLoadingCustomers}
                items={reduxProps.customers || []}
                tableColumns={tblColumns}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
            />
        </PageCard>
    )
}
